import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import { PatchData } from "../Data/PatchData";
import * as SoundEffect from "../Data/SoundEffect";
import { ColourRampFilter } from "../Filters/ColourRampFilter";
import { PatchTile } from "../GameObjects/PatchTile";
import * as QuiltUtils from "../Utils/QuiltUtils";

export class Patch extends PIXI.Container {
    private static readonly DECAL_SUFFIX: string = "-decal";

    private _base: PIXI.Sprite;
    private _baseMask: PIXI.Graphics;
    private _decal: PIXI.Sprite | undefined;

    private _colourRamp: PIXI.Texture | undefined;
    private _filter: ColourRampFilter | undefined;
    private _tiles: PatchTile[][];
    private _data: PatchData;

    constructor(pData: PatchData) {
        super();
        this._data = pData;

        this._base = HLF.PixiUtils.makeSprite(this._data.asset, this._data.sheet);
        this._base.rotation = this._data.rotation * Math.PI * 0.5;
        this._base.anchor.set(
            (this._data.rotation === 0 || this._data.rotation === 1) ? 0 : 1,
            (this._data.rotation === 0 || this._data.rotation === 3) ? 0 : 1,
        );
        this._base.scale.set(this._data.scale);
        this.addChild(this._base);

        if (this._data.allowDecal) {
            this._decal = HLF.PixiUtils.makeSprite(this._data.asset + Patch.DECAL_SUFFIX, this._data.sheet);
            this._decal.rotation = this._base.rotation;
            this._decal.anchor.copy(this._base.anchor);
            this._decal.scale.copy(this._base.scale);
            this.addChild(this._decal);
        }

        this._baseMask = new PIXI.Graphics();
        this._baseMask.beginFill(0x000000, 0);
        this._baseMask.drawPolygon(this._data.template.shape.rotatePolygon(this._data.rotation));
        this._baseMask.endFill();
        this._baseMask.scale.set(QuiltUtils.TILE_WIDTH);
        this.addChild(this._baseMask);
        this._base.mask = this._baseMask;

        const occupancy: boolean[][] = this._data.template.shape.rotateOccupancy(this._data.rotation);
        this._tiles = new Array<PatchTile[]>(occupancy.length);
        for (let y = 0; y < this._tiles.length; ++y) {
            this._tiles[y] = new Array<PatchTile>(occupancy[y].length);
            for (let x = 0; x < this._tiles[y].length; ++x) {
                if (occupancy[y][x] === true) {
                    this._tiles[y][x] = new PatchTile();
                    this._tiles[y][x].position.set(
                        QuiltUtils.TILE_WIDTH * x,
                        QuiltUtils.TILE_WIDTH * y,
                    );
                    this.addChild(this._tiles[y][x]);
                }
            }
        }
        this.constructBorders();

        if (this._data.rampIndex !== undefined) {
            const texture: string =
                "colour-ramp-" +
                HLF.NumberUtils.addZeroPadding(this._data.rampIndex.toString(), 2);
            this._colourRamp = PIXI.loader.resources[texture].texture;
            this._filter = new ColourRampFilter(this._colourRamp);
            this._base.filters = [ this._filter ];
        }
    }

    public get tiles(): PatchTile[][] {
        return this._tiles;
    }

    public get tileWidth(): number {
        return this._tiles[0].length;
    }

    public get tileHeight(): number {
        return this._tiles.length;
    }

    public get data(): PatchData {
        return this._data;
    }

    public get obscured(): boolean {
        for (let y = 0; y < this.tileHeight; ++y) {
            for (let x = 0; x < this.tileWidth; ++x) {
                if (this._tiles[y][x] !== undefined && this._tiles[y][x].obscured === false) {
                    return false;
                }
            }
        }
        return true;
    }

    public showStitches(): void {
        for (let y = 0; y < this.tileHeight; ++y) {
            for (let x = 0; x < this.tileWidth; ++x) {
                if (this._tiles[y][x] !== undefined) {
                    this._tiles[y][x].showStitches(this.getEdges(x, y));
                }
            }
        }
    }

    public onTileObscured(pX: number, pY: number): void {
        this._tiles[pX][pY].obscured = true;
    }

    public onBorderObscured(pX: number, pY: number, pDir: QuiltUtils.Direction): void {
        this._tiles[pX][pY].hideStitches(pDir);
    }

    public clone(): Patch {
        return new Patch(this._data);
    }

    private constructBorders(): void {
        for (let y = 0; y < this.tileHeight; ++y) {
            for (let x = 0; x < this.tileWidth; ++x) {
                if (this._tiles[y][x] !== undefined) {
                    this.constructBordersAtTile(x, y);
                }
            }
        }
    }

    private constructBordersAtTile(pX: number, pY: number): void {
        for (let dir = 0; dir < QuiltUtils.Direction.NUM_ELEMENTS; ++dir) {
            if (this.isDirectionOccupied(pX, pY, dir) === false) {
                this._tiles[pY][pX].addBorder(dir);
            }
        }
    }

    private isDirectionOccupied(pX: number, pY: number, pDir: QuiltUtils.Direction): boolean {
        const destX: number = pX + QuiltUtils.DIR_OFFSET[pDir].x;
        const destY: number = pY + QuiltUtils.DIR_OFFSET[pDir].y;
        return (
            destX >= 0 && destX < this._tiles[0].length &&
            destY >= 0 && destY < this._tiles.length &&
            this._tiles[destY][destX] !== undefined
        );
    }

    private getEdges(pX: number, pY: number): QuiltUtils.Direction[] {
        const directions: QuiltUtils.Direction[] = new Array<QuiltUtils.Direction>();
        const pos: PIXI.Point = QuiltUtils.localToGrid(this.position);
        if ((pos.x + pX) === 0) {
            directions.push(QuiltUtils.Direction.WEST);
        }
        if ((pos.x + pX) === QuiltUtils.GRID_SIZE.x - 1) {
            directions.push(QuiltUtils.Direction.EAST);
        }
        if ((pos.y + pY) === 0) {
            directions.push(QuiltUtils.Direction.NORTH);
        }
        if ((pos.y + pY) === QuiltUtils.GRID_SIZE.y - 1) {
            directions.push(QuiltUtils.Direction.SOUTH);
        }
        return directions;
    }
}
